import type { ColumnVisibilityOption } from 'src/hooks/useColumnVisibility'

/**
 * The columns each chooser offers, in display order.
 *
 * Declared here rather than beside the tables so `UIContext` — which owns the
 * visibility state — does not have to import from `components/`, which imports
 * back from the context.
 *
 * Identity and action columns are deliberately absent from every list: the
 * division/team/player name is what identifies a row, and the action columns
 * are the only route to Add Player, Details, Move and Trade. Offering either
 * would let someone hide the table's own controls with no way back except
 * Reset.
 */

/** `colId` values must match the `key` on each antd column def. */
export const TEAM_CHOOSER_COLUMNS: ColumnVisibilityOption[] = [
  { colId: 'players_count', headerName: 'Regd.' },
  { colId: 'roster_players_count', headerName: 'Plyrs.' },
  { colId: 'registered_at', headerName: 'Regd. At' },
  { colId: 'billing_schedule', headerName: 'Due' },
  { colId: 'deposit_paid', headerName: 'Dep. Paid' },
  { colId: 'outstanding', headerName: 'Past Due' },
  { colId: 'paid', headerName: 'Paid' },
  { colId: 'total_due', headerName: 'Total Outstg.' },
  { colId: 'total_paid', headerName: 'Total Paid' },
  { colId: 'status', headerName: 'Status' },
]

export const PLAYER_CHOOSER_COLUMNS: ColumnVisibilityOption[] = [
  { colId: 'age', headerName: 'Age' },
  { colId: 'registrationDate', headerName: 'Regd. At' },
  { colId: 'isDepositPaid', headerName: 'Dep. Paid' },
  { colId: 'outstandingValue', headerName: 'Outstg.' },
  { colId: 'paid', headerName: 'Paid' },
  { colId: 'status', headerName: 'Status' },
]

/**
 * The division header is a row of summary cells, not a table, so these ids are
 * render guards rather than column keys.
 *
 * `deposit` covers both the Deposit cell and the One Time Pymt cell — they are
 * the same slot, shown according to the division's payment setup, so one
 * toggle governs whichever is rendered.
 */
export const DIVISION_CHOOSER_COLUMNS: ColumnVisibilityOption[] = [
  { colId: 'gender', headerName: 'Gender' },
  { colId: 'players', headerName: 'Players' },
  { colId: 'teams', headerName: 'Teams' },
  { colId: 'drafts', headerName: 'Drafts' },
  { colId: 'deposit', headerName: 'Deposit' },
  { colId: 'team_cost', headerName: 'Team Cost' },
  { colId: 'player_cost', headerName: 'Player Cost' },
  { colId: 'revenue', headerName: 'Revenue' },
]

/** localStorage keys — one per chooser, so the three stay independent. */
export const COLUMN_STORAGE_KEYS = {
  division: 'sn.registration.columns.division',
  teams: 'sn.registration.columns.teams',
  players: 'sn.registration.columns.players',
} as const
